<template>
  <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent">
  <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
  <main class="view-container" :class="{ 'no-footer': route.name === 'wrapped' }">
    <router-view v-slot="{ Component, route }">
      <transition :name="transitionName">
        <component :is="Component" :key="route.path" class="full-height" />
      </transition>
    </router-view>
  </main>
  <FooterMenu v-if="route.name !== 'wrapped'"></FooterMenu>
  <div v-if="isStaging" class="staging-indicator">STAGING MODE</div>
</template>

<style>
:root {
  --primary-color: #000000;
  --secondary-color: #1c1c1e;
  --tertiary-color: #8E8E93;
  --lighten-primary: rgba(0,0,0,0.4);
  --background: #FFFFFF;
  --background-secondary: #F2F2F7;
  --warning: #FF3B30;
  --success: #34C759;
  --border-color: #E5E5EA;
  --glass-bg: rgba(255, 255, 255, 0.1);
  --glass-bg-inverted: rgba(28, 28, 30, 0.85);
  --lighten-bg: rgba(255, 255, 255, 0.75);
  --active-bg: #E5E5EA;
  --text-on-primary: #FFFFFF;
  --icon-filter: none;
  --icon-filter-invert: invert(1);

  --font: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;

  --mid-width: clamp(200px, 90vw, 500px);
  --max-width: clamp(200px, 90vw, 600px);
  
  --radius-l: 18px;
  --radius-m: 12px;
  --radius-s: 8px;
}

[data-theme='dark'] {
  --primary-color: #FFFFFF;
  --secondary-color: #E5E5EA;
  --tertiary-color: #8E8E93;
  --lighten-primary: rgba(255,255,255,0.4);
  --background: #000000;
  --background-secondary: #1c1c1e;
  --warning: #FF453A;
  --success: #32D74B;
  --border-color: #38383a;
  --glass-bg: rgba(28, 28, 30, 0.85);
  --glass-bg-inverted: rgba(255, 255, 255, 0.1);
  --lighten-bg: rgba(0, 0, 0, 0.75);
  --active-bg: #2c2c2e;
  --text-on-primary: #000000;
  --icon-filter: invert(1);
  --icon-filter-invert: none;
}

body {
  margin: 0;
  background: var(--background);
  color: var(--primary-color);
  overscroll-behavior: contain;
  overflow: hidden;
  font-family: var(--font);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

#selected-pool, .selected-pool {
  text-align: center;
}

.back-button img {
  filter: var(--icon-filter);
}

.full-height {
  height: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  /* Verhindert das Springen der Breite durch den Scrollbalken */
  scrollbar-gutter: stable;
  /* Fix: Seite dauerhaft auf GPU-Ebene halten */
  transform: translate3d(0, 0, 0);
  -webkit-transform: translate3d(0, 0, 0);
  backface-visibility: hidden;
}

#app {
  font-family: var(--font);
  text-align: center;
  background: var(--background);
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  color: var(--primary-color);
}

.main {
  background: transparent !important;
}

.frosted {
  background: var(--glass-bg) !important;
  backdrop-filter: blur(10px) !important;
  -webkit-backdrop-filter: blur(10px) !important;
  border-radius: var(--radius-m) !important;
  border: 1px solid var(--border-color) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
  /* Fix: Verhindert Flackern/Springen nach Animation */
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  transform: translateZ(0); 
}

.frosted.inverted {
  background: var(--glass-bg-inverted) !important;
}

.view-container {
  flex-grow: 1;
  position: relative;
  overflow: hidden;
  height: 100%;
  background: var(--background) url(@/assets/images/clink-bg.png) no-repeat !important;
  background-size: cover !important;
  background-position: center !important;
  padding-bottom: 90px; /* Platz für Footer auf Mobile */
  box-sizing: border-box;
}

.view-container.no-footer {
  padding-bottom: 0 !important;
}

@media only screen and (min-width: 600px) {
  #app {
    flex-direction: row-reverse;
  }

  .view-container {
    padding-bottom: 0;
  }

  .full-height {
    flex-grow: 1;
  }
}

button {
  cursor: pointer;
  font-family: var(--font);
  border: none;
  background: none;
}

h1, h2, h3, h4, h5, h6 {
  font-weight: 700;
  letter-spacing: -0.5px;
  color: var(--primary-color);
}

.error {
  color: var(--warning);
}

.blur {
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.fade-gradient {
  position: relative;
  display: inline-block !important;
}

.fade-gradient::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 100%);
  pointer-events: none;
}

.disabled_loading {
  position: relative;
  display: inline-block !important;
}

.disabled_loading::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255,255,255,0.6);
  pointer-events: none;
  border-radius: var(--radius-m);
}

.center-msg {
  margin-top: 50%;
}

/* Slide Transitions */
.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 90px;
  will-change: transform;
}

@media only screen and (min-width: 600px) {
  .slide-left-enter-active,
  .slide-left-leave-active,
  .slide-right-enter-active,
  .slide-right-leave-active {
    height: 100%;
  }
}

/* Slide Left (Moving Next) */
.slide-left-enter-from {
  transform: translate3d(100%, 0, 0);
}
.slide-left-leave-to {
  transform: translate3d(-100%, 0, 0);
}

/* Slide Right (Moving Back) */
.slide-right-enter-from {
  transform: translate3d(-100%, 0, 0);
}
.slide-right-leave-to {
  transform: translate3d(100%, 0, 0);
}

/* Ensure container handles absolute transitions */
#app {
  position: relative;
  overflow: hidden;
}

.staging-indicator {
  position: fixed;
  bottom: 100px;
  right: 10px;
  background: orange;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: bold;
  z-index: 9999;
  pointer-events: none;
  opacity: 0.8;
}
</style>
<script setup>
import { onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

const route = useRoute();
const transitionName = ref('slide-left');
const prevDepth = ref(0);
const isStaging = ref(false);

onMounted(() => {
  isStaging.value = window.location.hostname === 'staging.clinkapp.de';
  const savedTheme = localStorage.getItem('theme') || 'light';
  document.documentElement.setAttribute('data-theme', savedTheme);
});

watch(
  () => route.meta.depth,
  (newDepth, oldDepth) => {
    // Falls oldDepth beim ersten Mal undefined ist, nehmen wir den gespeicherten prevDepth
    const fromDepth = oldDepth !== undefined ? oldDepth : prevDepth.value;
    const toDepth = newDepth || 0;
    
    transitionName.value = toDepth < fromDepth ? 'slide-right' : 'slide-left';
    prevDepth.value = toDepth;
  }
);


</script>
